import {
  AfterViewInit, ChangeDetectionStrategy,
  Component,
  EventEmitter,
  input, InputSignal, OnChanges,
  OnDestroy,
  OnInit,
  Output,
  output, SimpleChanges,
  ViewChild
} from '@angular/core';
import {Subscription} from "rxjs";
import {SnackbarService} from "../../../services/snackbar.service";
import {LanguageService} from "../../../services/language.service";
import {LangChangeEvent, TranslateModule, TranslateService} from "@ngx-translate/core";
import {CardsBasicComponent} from "../../cards/cards-basic/cards-basic.component";
import {FormsModule} from "@angular/forms";
import {MatButton, MatIconButton} from "@angular/material/button";
import {MatCheckbox, MatCheckboxModule} from "@angular/material/checkbox";
import {MatFormField, MatLabel} from "@angular/material/form-field";
import {MatInput} from "@angular/material/input";
import {MatTableDataSource, MatTableModule} from "@angular/material/table";
import {MatIconModule} from "@angular/material/icon";
import {MatChipsModule} from "@angular/material/chips";
import {MatSelectModule} from "@angular/material/select";
import {SelectionModel} from "@angular/cdk/collections";
import {MatPaginator, MatPaginatorModule} from "@angular/material/paginator";
import {NgForOf, NgIf} from "@angular/common";
import * as Papa from 'papaparse';
import {CdkDrag, CdkDragDrop, CdkDropList, moveItemInArray} from "@angular/cdk/drag-drop";
import {MatTooltip} from "@angular/material/tooltip";

interface TableData {
  headers: string[];
  rows: string[][];
}

interface DataRow {
  [key: string]: any;
}

@Component({
  selector: 'app-table-editor',
  standalone: true,
  imports: [
    CardsBasicComponent,
    FormsModule,
    MatButton,
    MatCheckbox,
    MatFormField,
    MatInput,
    MatLabel,
    TranslateModule,
    MatTableModule,
    MatIconModule,
    MatChipsModule,
    MatIconModule,
    MatSelectModule,
    MatPaginator,
    MatCheckboxModule,
    NgForOf,
    NgIf,
    MatPaginatorModule,
    MatChipsModule,
    CdkDropList,
    CdkDrag,
    MatIconButton,
    MatTooltip
  ],
  templateUrl: './table-editor.component.html',
  styleUrl: './table-editor.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TableEditorComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
  isLoading: boolean = false;
  displayedColumns: string[] = [];
  dataSource = new MatTableDataSource<any>([]);
  selection = new SelectionModel<DataRow>(true, []);
  currentLanguage!: string;
  langChangeSubscription: Subscription | null = null;

  language = input();
  title = input();
  dataInput: InputSignal<any> = input();
  @Output() outputChange = new EventEmitter<unknown>();
  @Output() copyDataOutput = new EventEmitter<unknown>();
  @Output() pasteChange = new EventEmitter<unknown>();
  @ViewChild(MatPaginator) paginator!: MatPaginator;


  private subscriptions: Subscription = new Subscription();

  constructor(
    private _snackbarService: SnackbarService,
    public _languageService: LanguageService,
    private _translate: TranslateService) { }

  ngOnInit() {
    this.setupLanguageSubscription();
    if(this.dataInput()){
      this.setDisplayedColumns();
    } else {
      this.displayedColumns.unshift('select');
    }
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['dataInput']) {
      this.setDisplayedColumns();
    }
  }

  private setTitle(){

  }

  private setDisplayedColumns(): void {
    const dataInput = this.dataInput();  // Ensure dataInput is correctly retrieved
    if (dataInput && dataInput.rows && dataInput.headers) {
      this.dataSource.data = dataInput.rows;  // Set the rows
      this.displayedColumns = [...dataInput.headers];  // Copy the headers array
      this.displayedColumns.unshift('select');  // Add 'select' column for selection
    }
  }

  private setupLanguageSubscription(): void {
    this.currentLanguage = this._languageService.getLanguage();
    this.subscriptions.add(
      this.langChangeSubscription = this._translate.onLangChange.subscribe((event: LangChangeEvent) => {
        this.currentLanguage = event.lang;
      })
    );
  }

  importCSV(event: any) {
    this.isLoading = true;
    const file = event.target.files[0];
    if (file) {
      Papa.parse(file, {
        header: true,
        complete: (results) => {
          const fields = results.meta.fields || [];

          // Filter out empty rows where all fields are empty, null, or undefined
          const rows = results.data.filter((row: any) => {
            return fields.some(field => row[field] !== '' && row[field] !== null && row[field] !== undefined);
          }).map((row: any) => fields.map(field => row[field]));

          this.displayedColumns = fields;
          this.displayedColumns.unshift('select');
          this.dataSource.data = rows;
          this.dataSource.paginator = this.paginator; // Set paginator
          this.paginator.pageIndex = 0; // Reset page index to the first page
          this.isLoading = false;
          this.emitData();
        }
      });
    }
  }


  addColumn() {
    let columnName;
    let suffix = this.displayedColumns.length + 1;

    do {
      columnName = `column${suffix}`;
      suffix++;
    } while (this.displayedColumns.includes(columnName));

    this.displayedColumns.push(columnName);
    this.dataSource.data.forEach(row => row.push(''));
    this.dataSource.paginator = this.paginator;
    this.emitData();
  }

  addRow() {
    const newRow: string[] = this.displayedColumns.filter(column => column !== 'select').map(() => '');
    this.dataSource.data = [...this.dataSource.data, newRow];
    this.dataSource.paginator = this.paginator;
    this.emitData();
  }

  updateCell(row: string[], columnIndex: number, event: any) {

    const newValue = event.target.textContent.trim();
    row[columnIndex] = newValue;
    event.target.textContent = newValue;

    this.emitData();
  }

  stopPropagation( event: Event){
    event.stopPropagation();
  }


  updateHeader(index: number, event: any) {
    const newColumnName = event.target.textContent.trim();
    const oldColumnName = this.displayedColumns[index];

    if (newColumnName !== oldColumnName) {
      let suffix = 1;
      let uniqueColumnName = newColumnName;

      while (this.displayedColumns.includes(uniqueColumnName)) {
        uniqueColumnName = newColumnName + suffix;
        suffix++;
      }

      this.displayedColumns[index] = uniqueColumnName;
      this.emitData();
    }
  }

  dropRow(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.dataSource.data, event.previousIndex, event.currentIndex);
    this.dataSource.data = [...this.dataSource.data]; // Update dataSource to trigger change detection
    this.emitData(); // Emit the updated data
  }

  drop(event: CdkDragDrop<string[]>) {
    // Move the column in the displayedColumns array
    moveItemInArray(this.displayedColumns, event.previousIndex, event.currentIndex);

    // Exclude 'select' from the columns to avoid affecting data reordering
    const dataWithoutSelect = this.displayedColumns.slice(1);

    // Rearrange the rows in dataSource according to the new order of displayedColumns
    this.dataSource.data = this.dataSource.data.map(row => {
      const reorderedRow: string[] = [];

      // Reorder row based on new header positions, excluding 'select'
      row.forEach((cell:any, originalIndex:any) => {
        const newIndex = originalIndex;

        // Adjust the index to match the new header position
        if (originalIndex === event.previousIndex - 1) {
          reorderedRow[event.currentIndex - 1] = cell;
        } else if (originalIndex >= event.currentIndex - 1 && originalIndex < event.previousIndex - 1) {
          reorderedRow[originalIndex + 1] = cell;
        } else if (originalIndex > event.previousIndex - 1 && originalIndex <= event.currentIndex - 1) {
          reorderedRow[originalIndex - 1] = cell;
        } else {
          reorderedRow[originalIndex] = cell;
        }
      });

      return reorderedRow;
    });

    this.emitData(); // Emit the updated data
  }




  deleteColumn(index: number) {
    this.displayedColumns.splice(index, 1);
    this.dataSource.data.forEach(row => row.splice(index, 1));
    this.emitData();
  }

  deleteSelectedRows() {
    this.selection.selected.forEach(row => {
      const index = this.dataSource.data.indexOf(row);
      if (index > -1) {
        this.dataSource.data.splice(index, 1);
      }
    });
    this.selection.clear();
    this.dataSource.data = [...this.dataSource.data];
    this.emitData();
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  toggleAllRows() {
    if (this.isAllSelected()) {
      this.selection.clear();
      return;
    }
    this.selection.select(...this.dataSource.data);
  }

  checkboxLabel(row?: string[]): string {
    if (!row) {
      return `${this.isAllSelected() ? 'deselect' : 'select'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row`;
  }

  emitData() {
    // Filter out the "select" column from the headers
    const filteredHeaders = this.displayedColumns.filter(column => column !== 'select');

    // Emit the updated table data
    const tableData: TableData = {
      headers: filteredHeaders,
      rows: this.dataSource.data
    };

    this.outputChange.emit(tableData);
  }

  copyData(){
    const filteredHeaders = this.displayedColumns.filter(column => column !== 'select');

    // Emit the updated table data
    const tableData: TableData = {
      headers: filteredHeaders,
      rows: this.dataSource.data
    };

    this.copyDataOutput.emit(tableData);
  }

  pasteData(){
    this.pasteChange.emit();
  }

  ngOnDestroy() {
    if (this.langChangeSubscription) {
      this.langChangeSubscription.unsubscribe();
    }
  }
}
